import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import toast from 'react-hot-toast';
import { Button } from "../../../components/Button/Button";
import ExpertSelectionList from "../components/ExpertSelectionList";
import ExpertSelectionCard from "../components/ExpertSelectionCard";
import axiosInstant from "../../../api";
import { getBrandProject, getProjectPros } from "../../../api/endpoints";
import { DotLottleLoader } from "../../../components/Loader";
import { BRAND_PROJECT_STATUSES, sortExpertsBySelection, describeSharedSiblings } from "../helper";
import { useSearchParams } from "react-router-dom";
import { useProcessSiblings } from "../../../hooks/useProcessSiblings";

const ExpertSelectionRecommendationDialog = ({ onConfirm }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedExperts, setSelectedExperts] = useState([]);
  const [projectPros, setProjectPros] = useState([]);
  const [project, setProject] = useState(null);
  const [brandId, setBrandId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // Experts are shared across the project's selection process; used to label
  // the shared scope.
  const sharedProcess = useProcessSiblings(
    project?.order_id != null ? Number(project.order_id) : undefined,
    project?.id,
    { siblingCount: project?.process_project_count }
  );
  const otherProcessProjects = (sharedProcess.siblingProjects || []).filter(
    (p) => Number(p.id) !== Number(project?.id)
  );

  const fetchProjectPros = async () => {
    if (project?.id && brandId) {
      const url = getProjectPros(brandId, project.id);
      try {
        const res = await axiosInstant.get(url);
        const data = res.data.rows || [];
        setProjectPros(data);
      } catch (err) {
        console.error("Failed to fetch project pros:", err);
        setProjectPros([]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const onClose = () => {
    setSearchParams((prev) => {
      prev.delete("expertSelection");
      return prev;
    });
  };

  useEffect(() => {
    if (open && project && brandId) {
      setIsLoading(true);
      fetchProjectPros();
    }
  }, [open, project, brandId]);

  useEffect(() => {
    const projectId = searchParams.get("expertSelection");
    if (projectId) {
      setIsLoading(true);
      axiosInstant.get(getBrandProject(Number(projectId))).then((response) => {
        setProject(response.data);
        setBrandId(response.data?.brands?.find((b) => b.rmn !== true)?.id);
      });
    }
  }, [searchParams]);

  const handleConfirm = async () => {
    if (selectedExperts.length === 0) return;

    try {
      const clearPromises = projectPros.map((pro) => {
        return axiosInstant.patch(
          `/brands/${brandId}/projects/${project?.id}/pros/${pro.userid}/status`,
          { status: 999 }
        );
      });

      await Promise.all(clearPromises);

      const updatePromises = selectedExperts.map((userId, index) => {
        return axiosInstant.patch(
          `/brands/${brandId}/projects/${project?.id}/pros/${userId}/status`,
          { status: index + 1 }
        );
      });

      await Promise.all(updatePromises);

      // Ranking is shared — the API fans the → Scheduling transition out to
      // the sibling projects on its own.
      await axiosInstant.patch(`/brands/${brandId}/projects/${project?.id}`, {
        status: BRAND_PROJECT_STATUSES.SCHEDULING,
      });

      // Success is a toast, not a modal — close in place and let onConfirm
      // refresh the host view (the list re-pulls its rows, the detail page
      // re-pulls project + roster). No redirect.
      toast.success(
        'Expert selection saved. Our team will confirm scheduling and availability next.'
      );
      onConfirm();
      onClose();
    } catch (err) {
      console.error("Error updating expert priorities:", err);
      toast.error('Failed to confirm expert selection. Please try again.');
    }
  };

  const sortedPros = sortExpertsBySelection(projectPros);

  const topExpert = sortedPros[0];
  const otherExperts = sortedPros.slice(1);

  return (
    <>
      <Dialog open maxWidth onClose={onClose}>
        <DialogTitle className="border-b border-border-token pb-4">
          <div className="flex justify-between items-center">
            <Typography variant="h5">
              Expert Selection
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon className="text-text-2" />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className="!py-4">
          <div className="text-text-2 font-circular text-[14px] font-[450] leading-normal">
            Select your experts in order of preference. If your first choice is
            not available, we will reach out to the other preferences in order
            of your choices.
          </div>
          {sharedProcess.isShared && (
            <div className="mt-2 text-accent-text font-circular text-[13px] font-[450]">
              This selection applies to {describeSharedSiblings(otherProcessProjects)} that
              share this project's experts.
            </div>
          )}
          {isLoading ? (
            <div className="p-6 flex items-center justify-center h-[400px]">
              <DotLottleLoader fill={false} sx={{ width: 200, height: 200 }} />
            </div>
          ) : projectPros.length ? (
            <>
              {topExpert && (
                <>
                  <div className="text-accent-text font-circular text-[14px] font-[450] mt-[24px] mb-[16px]">
                    Top Recommendation!
                  </div>
                  <ExpertSelectionList
                    recommendationCheck={true}
                    pros={topExpert}
                    project={project}
                    onUpdate={fetchProjectPros}
                    selectedExperts={selectedExperts}
                    setSelectedExperts={setSelectedExperts}
                    sharedSiblingProjects={otherProcessProjects}
                  />
                </>
              )}

              {otherExperts.length > 0 && (
                <>
                  <div className="text-text-2 font-circular text-[14px] font-[450] my-[16px]">
                    Other Recommended Experts
                  </div>

                  <Box sx={{ overflowX: "auto", width: "100%", padding: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "center", sm: "stretch" },
                        gap: "16px",
                        minWidth: { xs: 0, sm: "fit-content" },
                        '& > *': {
                          width: { xs: "100%", sm: 300 },
                          flexShrink: 0,
                        },
                      }}
                    >
                      {otherExperts.map((pro) => (
                        <ExpertSelectionCard
                          key={pro.userid}
                          pro={pro}
                          isSelected={selectedExperts.includes(pro.userid)}
                          recommendationCheck={true}
                          project={project}
                          onUpdate={fetchProjectPros}
                          onScoreChange={() => {}}
                          selectedExperts={selectedExperts}
                          setSelectedExperts={setSelectedExperts}
                          approvalCheck={true}
                          isApprovalPage={false}
                          sharedSiblingProjects={otherProcessProjects}
                        />
                      ))}
                    </Box>
                  </Box>
                </>
              )}
            </>
          ) : (
            <div className="p-6 flex items-center justify-center h-[400px]">
              <div className="text-text-2 font-circular text-[14px] font-[450] leading-normal">
                No experts found
              </div>
            </div>
          )}
        </DialogContent>

        <DialogActions className="max-sm:!px-4 !px-6 !py-4 border-t border-border-token">
          <div className="flex max-sm:flex-col max-sm:items-start max-sm:gap-3 justify-between items-center w-full">
            <span className="text-text-2 text-[14px] font-circular">
              For Expert related questions, please contact our{" "}
              <a
                href="mailto:experts@thedesirecompany.com"
                target="_blank"
                rel="noreferrer"
                className="text-accent-text hover:underline cursor-pointer"
              >
                support team
              </a>
            </span>
            <Button
              variant="contained"
              onClick={handleConfirm}
              className="h-[40px] flex !py-0 !px-4 !min-w-fit"
              disabled={!selectedExperts.length}
            >
              Confirm
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExpertSelectionRecommendationDialog;
